import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

// Pages
import OnePage from './pages/OnePage';
import Contact from './pages/Contact';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activePage, setActivePage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isContactPage, setIsContactPage] = useState(location.pathname === '/contact');

  // Gestion du redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effet pour suivre les changements d'URL avec useLocation
  useEffect(() => {
    setIsContactPage(location.pathname === '/contact');
  }, [location]);

  // Fonction pour mettre à jour la section active et faire défiler vers cette section
  const handleSectionChange = (section: string) => {
    // Si on est sur la page de contact, naviguer directement vers la page d'accueil avec un ancre
    if (isContactPage) {
      // Utiliser window.location.href pour une redirection directe vers l'ancre
      window.location.href = `/#${section}`;
      return;
    }
    
    // Si on est sur la page d'accueil, faire défiler normalement
    setActivePage(section);
    setMobileMenuOpen(false);
    
    // Faire défiler vers la section sélectionnée
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Détecter la section visible lors du défilement
  useEffect(() => {
    const handleScroll = () => {
      // Ne pas exécuter sur la page de contact
      if (isContactPage) return;
      
      const sections = ['home', 'social-media', 'product-promotion', 'scenography', 'full-video', '2d-video'];
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActivePage(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isContactPage]);

  return (
      <div className="App bg-white text-black min-h-screen font-poppins flex flex-col overflow-y-auto">
        {!isContactPage && (
        <header className="py-4 md:py-6 px-4 md:px-8 flex flex-col lg:flex-row justify-between items-start lg:items-center">
          <div className="flex justify-between items-center relative z-50 w-full lg:w-auto">
            <div 
              className="text-xl font-light cursor-pointer" 
              onClick={() => handleSectionChange('home')}
            >
              <h1>Noah Desmoulieres</h1>
              <p className="text-sm text-gray-600">Portfolio</p>
            </div>
            
            {/* Menu hamburger simplifié */}
            <button 
              className="lg:hidden relative z-50 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <div className={`w-5 h-0.5 bg-black absolute transition-transform duration-200 ${mobileMenuOpen ? 'rotate-45 translate-y-[2px]' : ''}`} style={{ top: 'calc(50% - 4px)' }} />
              <div className={`w-5 h-0.5 bg-black absolute transition-opacity duration-200 ${mobileMenuOpen ? 'opacity-0' : ''}`} style={{ top: 'calc(50%)' }} />
              <div className={`w-5 h-0.5 bg-black absolute transition-transform duration-200 ${mobileMenuOpen ? '-rotate-45 -translate-y-[2px]' : ''}`} style={{ top: 'calc(50% + 4px)' }} />
            </button>
          </div>
          
          {/* Navigation - version desktop uniquement */}
          <nav className="hidden lg:flex lg:flex-row lg:space-x-6 lg:space-y-0 lg:w-auto">
            <button 
              onClick={() => handleSectionChange('social-media')}
              className={`nav-link ${activePage === 'social-media' ? 'text-black' : 'text-gray-500'}`}
            >
              SOCIAL MEDIA 3D TEASER
            </button>
            <button 
              onClick={() => handleSectionChange('product-promotion')}
              className={`nav-link ${activePage === 'product-promotion' ? 'text-black' : 'text-gray-500'}`}
            >
              PRODUCT PROMOTION
            </button>
            <button 
              onClick={() => handleSectionChange('scenography')}
              className={`nav-link ${activePage === 'scenography' ? 'text-black' : 'text-gray-500'}`}
            >
              SCENOGRAPHIE SCREEN ANIMATION
            </button>
            <button 
              onClick={() => handleSectionChange('full-video')}
              className={`nav-link ${activePage === 'full-video' ? 'text-black' : 'text-gray-500'}`}
            >
              FULL VIDEO CLIP / EDIT
            </button>
            <button 
              onClick={() => handleSectionChange('2d-video')}
              className={`nav-link ${activePage === '2d-video' ? 'text-black' : 'text-gray-500'}`}
            >
              2D VIDEO / FLYER
            </button>
          </nav>
          
          {/* Menu mobile - uniquement visible sur mobile et lorsqu'il est ouvert */}
          {mobileMenuOpen && (
            <div className="fixed inset-0 z-40 bg-white pt-24 px-6 pb-6 lg:hidden overflow-y-auto">

              <div className="flex flex-col space-y-5 max-w-md mx-auto">

                <button 
                  onClick={() => handleSectionChange('social-media')}
                  className={`text-left py-2 px-3 rounded-md transition-colors duration-200 ${activePage === 'social-media' ? 'text-black font-medium bg-gray-50' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  SOCIAL MEDIA 3D TEASER
                </button>
                <button 
                  onClick={() => handleSectionChange('product-promotion')}
                  className={`text-left py-2 px-3 rounded-md transition-colors duration-200 ${activePage === 'product-promotion' ? 'text-black font-medium bg-gray-50' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  PRODUCT PROMOTION
                </button>
                <button 
                  onClick={() => handleSectionChange('scenography')}
                  className={`text-left py-2 px-3 rounded-md transition-colors duration-200 ${activePage === 'scenography' ? 'text-black font-medium bg-gray-50' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  SCENOGRAPHIE SCREEN ANIMATION
                </button>
                <button 
                  onClick={() => handleSectionChange('full-video')}
                  className={`text-left py-2 px-3 rounded-md transition-colors duration-200 ${activePage === 'full-video' ? 'text-black font-medium bg-gray-50' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  FULL VIDEO CLIP / EDIT
                </button>
                <button 
                  onClick={() => handleSectionChange('2d-video')}
                  className={`text-left py-2 px-3 rounded-md transition-colors duration-200 ${activePage === '2d-video' ? 'text-black font-medium bg-gray-50' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  2D VIDEO / FLYER
                </button>
                
                <div className="h-px bg-gray-200 my-3"></div>
                
                <button 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/contact');
                  }}
                  className="flex items-center justify-center space-x-2 bg-black text-white py-3 px-4 rounded-md mt-2 w-full hover:bg-gray-800 transition-colors duration-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span>Me contacter</span>
                </button>
              </div>
            </div>
          )}
          
          {/* CTA Formulaire de contact - masqué sur mobile, visible sur desktop */}
          <div className="hidden lg:flex items-center">
            <button 
              onClick={() => navigate('/contact')}
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors duration-300 flex items-center space-x-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <span>Me contacter</span>
            </button>
          </div>
        </header>
        )}

        <main className="flex-grow overflow-y-auto">
          <Routes>
            <Route path="/" element={<OnePage />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>

        <footer className="mt-auto py-6 px-4 md:px-8 text-center text-gray-500 text-sm border-t border-gray-100">
          &copy; 2025 Noah Desmoulieres. All rights reserved.
        </footer>
      </div>
  );
}


function App() {
  return (
    <Router>
      <div className="App">
        <AppContent />
      </div>
    </Router>
  );
}

export default App;
